import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import Table from '../common/Table';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './ViewVacantBeds.css';

const ViewVacantBeds = () => {
  const [vacantBeds, setVacantBeds] = useState([]);
  const [totalVacantBeds, setTotalVacantBeds] = useState(0);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('all');
  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [loadingBeds, setLoadingBeds] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch buildings on component mount
  useEffect(() => {
    fetchBuildings();
  }, []);

  // Fetch vacant beds when building selection changes
  useEffect(() => {
    fetchVacantBeds();
  }, [selectedBuildingId]);

  const fetchBuildings = async () => {
    try {
      setLoadingBuildings(true);
      const response = await apiGet('/building/names');
      if (response.status === 200) {
        setBuildings(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
      toastError('Failed to fetch buildings');
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchVacantBeds = async () => {
    try {
      setLoadingBeds(true);
      const url = selectedBuildingId === 'all' 
        ? '/building/vacant_beds' 
        : `/building/vacant_beds/${selectedBuildingId}`;
      
      const response = await apiGet(url);
      if (response.status === 200) {
        const data = response.data || {};
        setVacantBeds(data.vacant_beds || []);
        setTotalVacantBeds(data.total_vacant_beds || 0);
        if (!data.vacant_beds || data.vacant_beds.length === 0) {
          toastError('No vacant beds found');
        }
      }
    } catch (error) {
      console.error('Error fetching vacant beds:', error);
      toastError('Failed to fetch vacant beds');
      setVacantBeds([]);
      setTotalVacantBeds(0);
    } finally {
      setLoadingBeds(false);
    }
  };

  const handleBuildingChange = (e) => {
    setSelectedBuildingId(e.target.value);
  };

  const handleViewBuilding = (row) => {
    navigate(`/building/view/${row.building_id}`);
  };

  const handleAllocateBed = (row) => {
    // Navigate to AllocateBed with state containing the selected bed info
    navigate('/bedallocation/allocate-bed', { 
      state: { 
        selectedBed: row
      }
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const columns = [
    {
      Header: 'Building',
      accessor: 'building_name'
    },
    {
      Header: 'Room',
      accessor: 'room_name'
    },
    {
      Header: 'Vacant Beds',
      accessor: 'vacant_beds',
      Cell: ({ row }) => (
        <span className="vacant-beds-count">
          {row.vacant_beds} of {row.number_of_beds}
        </span>
      ),
      exportValue: row => `${row.vacant_beds} of ${row.number_of_beds}`,
    },
    {
      Header: 'Room Mates',
      accessor: 'room_mates',
      Cell: ({ value }) => value && value.length > 0 ? value.join(', ') : 'None',
      exportValue: row => row.room_mates && row.room_mates.length > 0 ? row.room_mates.join(', ') : 'None',
    },
    {
      Header: 'Bed Rent',
      accessor: 'bed_rent',
      Cell: ({ value }) => `₹${value}`,
    },
    {
      Header: 'Action',
      accessor: 'action',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <RTButton
            style={{ width: 'auto' }}
            type='link'
            onClick={() => handleViewBuilding(row)}
          >
            View 
          </RTButton>
          <RTButton 
            type='link'
            onClick={() => handleAllocateBed(row)}
          >
            Allocate Bed
          </RTButton>
        </div>
      ),
      exportValue: () => '', // No export for actions
    },
  ];

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">View Vacant Beds</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={handleBack}>
              &#8592; Back
            </RTButton>
          </div>
        </div>

        <div className="rt-main-section">
          {/* Filter Section */}
          <div className="rt-filter-section">
            <div className="rt-form-group">
              <label htmlFor="building-filter">Filter by Building:</label>
              {loadingBuildings ? (
                <div className="filter-loading">
                  <RTSpinner size="small" fullScreen={true} />
                </div>
              ) : (
                <select
                  id="building-filter"
                  className="rt-input"
                  value={selectedBuildingId}
                  onChange={handleBuildingChange}
                >
                  <option value="all">All Buildings</option>
                  {buildings.map((building) => (
                    <option key={building.building_id} value={building.building_id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className="rt-table-container">
            {loadingBeds ? (
              <div className="loading-container">
                <RTSpinner fullScreen={true} />
                <p>Loading vacant beds...</p>
              </div>
            ) : (
              <Table
                title={selectedBuildingId === 'all' 
                  ? `Vacant Beds - All Buildings (${totalVacantBeds} Total)` 
                  : `Vacant Beds - ${buildings.find(b => b.building_id === parseInt(selectedBuildingId))?.name || ''} (${totalVacantBeds} Total)`
                }
                columns={columns}
                data={vacantBeds}
                showSearch={true}
                exportable={true}
                showPagination={true}
                noDataText="No vacant beds available"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewVacantBeds;